import type { FaqEntriesByLocale } from './faq';

export type { FaqEntry } from './faq';

/**
 * ### INDUSTRIAL_FAQ_BY_LOCALE
 *
 * Listen AI Industrial FAQ copy, rendered on the solution page and emitted as
 * FAQPage structured data from the same strings so the two can never drift.
 * Editing an entry changes both surfaces at once; a figure changed here is a
 * public performance claim and needs the same review as press material.
 *
 * Entries are ordered the way a reader qualifies the solution: whether it works
 * in their environment, what it catches, how that is measured, then how it is
 * installed and how an evaluation starts.
 *
 * Two sets of figures appear on this page and never mix inside one sentence.
 * The entries here carry press-release results tied to a named customer and
 * release date (99.87%, 99.78%); the production figures in
 * {@link INDUSTRIAL_PRODUCTION_METRICS} are the press kit's own scoped claims.
 * A claim scoped to the connector engagement release says so in its own
 * sentence, because that scope is what keeps it from reading as a promise
 * about every deployment.
 *
 * @see {@link buildIndustrialStructuredData} Wraps these entries as schema.org Question nodes
 */
export const INDUSTRIAL_FAQ_BY_LOCALE: FaqEntriesByLocale = {
  ko: [
    {
      question: '85dB 이상의 시끄러운 공장 환경에서도 검사할 수 있나요?',
      answer:
        '가능합니다. Listen AI Industrial은 임팩트 렌치, 에어건, 금속 마찰음, 컨베이어 벨트 소음이 겹치는 85dB 이상 환경을 전제로 설계되었습니다. 디플리 자체 Foundation Model은 210만 시간 이상의 실제 공장 소음 데이터와 1,000만 건 이상의 공정 이벤트 데이터를 기반으로 하며, 작업자가 귀나 손끝 감각으로 판단하던 미세한 체결음을 소음 속에서 분리합니다.',
    },
    {
      question: '어떤 불량과 설비 이상을 감지할 수 있나요?',
      answer:
        'Listen AI Industrial의 기본 솔루션은 회전체 이음검사, 조립라인 커넥터 체결음 검사, 기계 노후화 검사 세 가지입니다. 이 외에 유리 파손·스크래치 감지, 반도체 부품 마모 진단, 로봇 설비 노후화 분석, 대형 엔진 구동부 노후화 분석, 가스·압력 밸브 소리 분석 등의 적용 사례가 있습니다.',
    },
    {
      question: '검사 정확도는 어떻게 정의하고 검증하나요?',
      answer:
        'Listen AI Industrial의 조립라인 커넥터 체결음 검사 정확도는 99.87%입니다. 글로벌 완성차 H사의 한국·멕시코 생산 라인에 적용한 결과이며, 2026년 6월 11일 보도자료 기준입니다. 회전체 이음검사는 별도의 산업 적용 사례에서 99.78% 정확도와 1초 이내 검사 시간이 보고되었습니다. 두 수치는 검사 항목과 측정 범위가 서로 다르므로 하나의 값으로 합쳐 쓰지 않습니다.',
    },
    {
      question: '커넥터 체결음 검사는 어떤 기준으로 OK/NG를 판정하나요?',
      answer:
        '주파수 대역 분석(Frequency-band Analysis)으로 판정합니다. 1차 락(Primary Lock)은 3~16kHz, 2차 락(Secondary Lock)은 7~16kHz 대역에서 체결음을 구분해 OK 또는 NG 결과를 반환합니다. 이를 통해 완전히 체결되지 않은 설체결과 소프트 커넥션을 걸러냅니다.',
    },
    {
      question: 'Cycle Time에 맞춘 인라인 전수검사가 가능한가요?',
      answer:
        '가능합니다. Listen AI Industrial은 제품당 약 1초 내외로 검사해 인라인(In-line) 전수검사를 수행하며, 매 시간 6,000개 제품, 매달 430만 개를 100% 인라인(In-line)으로 판정합니다. EOL 단계에서는 모터·액추에이터를 24/7 전수 검사합니다. 검사 시간과 판정 규모는 검사 항목과 공정 조건에 따라 달라지므로, 라인의 Cycle Time을 충족하는지는 PoC에서 확인합니다.',
    },
    {
      question: '기존 PLC·MES와 연동할 수 있나요?',
      answer:
        '연동할 수 있습니다. Listen AI Industrial은 검사 이력을 MES(생산관리시스템) 또는 PLC와 연결해 추적 가능한 데이터로 남깁니다. 배포 방식은 엣지 분석서버 방식과 온프렘 방식을 지원합니다.',
    },
    {
      question: '마이크와 분석 서버는 어떻게 구성되나요?',
      answer:
        '검사 대상과 현장 조건에 맞춘 마이크와 분석 서버를 조합해 구성합니다. 마이크는 고정형 지향성 마이크, 작업자가 착용하는 손목 밴드형 마이크, 고소음 구역을 위한 어레이 마이크 중에서 선택합니다. 분석 서버는 엣지 분석서버 방식과 온프렘 방식을 지원하며, OK/NG 판정 결과는 기존 MES(생산관리시스템) 또는 PLC와 실시간으로 연결해 추적 가능한 데이터로 남깁니다. 커넥터 체결음 검사 보도자료 기준으로는 기존 라인의 설비·공구·레이아웃을 바꾸지 않고 설치할 수 있다고 밝히고 있습니다.',
    },
    {
      question: '정상·불량 데이터가 적어도 PoC가 가능한가요?',
      answer:
        '가능합니다. Listen AI Industrial은 현장별 불량 데이터를 몇 주에서 몇 달에 걸쳐 모은 뒤에 시작하는 방식이 아니라, 디플리 Foundation Model을 기반으로 하드웨어 설치 직후부터 초기 평가를 시작합니다. 대상 설비의 체결음이나 작동음을 수집하면 그 위에서 평가를 진행할 수 있습니다. 다만 이는 제조 음향 데이터에 특화된 Foundation Model을 전제로 한 커넥터 체결음 검사 보도자료 기준이므로, 모든 도입이 데이터 수집 없이 가능하다는 뜻은 아닙니다. PoC 결과는 양산 라인 적용으로 이어집니다.',
    },
    {
      question: '품질검사와 예지보전은 무엇이 다른가요?',
      answer:
        '판정 대상과 검사 시점이 다릅니다. 품질검사는 생산되는 제품을 대상으로 합니다. 양산 라인에서는 커넥터·볼트의 연속 체결 작업을 실시간으로 감지하고, EOL 단계에서는 모터·액추에이터를 출하 전에 전수 검사해 불량 유출을 차단합니다. 예지보전(PdM)은 제품이 아니라 그 제품을 만드는 설비 자체를 대상으로 합니다. 베어링이나 가스 유출음 같은 설비 이상 신호를 사전에 감지해 다운타임을 막는 것이 목적이며, 특정 공정의 한 시점이 아니라 설비를 상시 모니터링하는 구간에서 판정합니다. 두 용도는 측정 범위가 다르므로 정확도 수치도 서로 옮겨 쓰지 않습니다.',
    },
  ],
  en: [
    {
      question: 'Can it inspect in a factory environment above 85 dB?',
      answer:
        'Yes. Listen AI Industrial is designed for environments above 85 dB where impact wrenches, air guns, metal friction, and conveyor belts overlap. Deeply’s proprietary Foundation Model is built on more than 2.1 million hours of real-world factory noise data and more than 10 million process event data points, which lets it separate the faint engagement sounds workers previously judged by ear or fingertip from the surrounding noise.',
    },
    {
      question: 'Which defects and equipment faults can it detect?',
      answer:
        'The three core Listen AI Industrial solutions are rotating-equipment noise inspection, assembly-line connector engagement sound inspection, and equipment aging detection. Deployed cases also cover glass breakage and scratch detection, semiconductor component wear diagnosis, robot equipment aging analysis, large engine drive-unit aging analysis, and gas and pressure valve sound analysis.',
    },
    {
      question: 'How is inspection accuracy defined and verified?',
      answer:
        'Listen AI Industrial reports 99.87% accuracy on assembly-line connector engagement sound inspection, measured on global automaker Company H production lines in Korea and Mexico and published in a June 11, 2026 press release. Rotating-equipment noise inspection is separately reported at 99.78% accuracy with sub-second inspection time in earlier industrial coverage. The two figures cover different inspection types and measurement scopes, so they are not combined into a single claim.',
    },
    {
      question:
        'How does connector engagement inspection decide between OK and NG?',
      answer:
        'It decides through Frequency-band Analysis. Listen AI Industrial distinguishes the Primary Lock in the 3–16 kHz band and the Secondary Lock in the 7–16 kHz band, then returns an OK or NG result. This is what catches half-clicks and soft connections that never fully engaged.',
    },
    {
      question: 'Can it run in-line 100% inspection at our Cycle Time?',
      answer:
        'Yes. Listen AI Industrial inspects in about one second per product to run in-line 100% inspection, judging 6,000 products an hour and 4.3 million a month fully in-line. At the EOL stage it inspects every motor and actuator 24/7. Inspection time and judgement volume shift with the inspection type and the process conditions, so whether a given line’s Cycle Time is met is confirmed during the PoC.',
    },
    {
      question: 'Can it integrate with our existing PLC and MES?',
      answer:
        'Yes. Listen AI Industrial links inspection history to a MES (Manufacturing Execution System) or PLC so results are retained as traceable data. Deployment is supported as an edge analysis server setup or on-premise.',
    },
    {
      question: 'How are the microphones and the analysis server set up?',
      answer:
        'The setup pairs microphones chosen for the target sound and the site with an analysis server. Microphones are selected from fixed directional microphones, wristband microphones worn by workers, and array microphones for high-noise areas. The analysis server is supported as an edge analysis server setup or on-premise, and OK/NG results are linked in real time with an existing MES (Manufacturing Execution System) or PLC so they are retained as traceable data. In the connector engagement press release, installation is described as requiring no change to the existing line’s equipment, tools, or layout.',
    },
    {
      question: 'Can we run a PoC with little normal or defect data?',
      answer:
        'Yes. Rather than waiting weeks or months while site-specific defect data is collected, Listen AI Industrial begins initial evaluation on Deeply’s Foundation Model from the moment the hardware is installed. Collecting the engagement or operating sounds of the target equipment is enough to evaluate on top of it. This is scoped to the connector engagement press release and its manufacturing-specific Foundation Model, so it does not mean every deployment can proceed without data collection. PoC results then carry into production-line deployment.',
    },
    {
      question:
        'How does quality inspection differ from predictive maintenance?',
      answer:
        'They judge different things at different moments. Quality inspection judges the product being made: on the production line it detects continuous connector and bolt engagement work in real time, and at the EOL stage it inspects every motor and actuator before shipment to block defect escapes. Predictive maintenance (PdM) judges the equipment that makes the product rather than the product itself. Its purpose is to catch equipment anomaly signals such as bearings or gas leak sound early enough to prevent downtime, and it judges from continuous monitoring of the equipment rather than at one point in a process. Because the two cover different measurement scopes, accuracy figures are not carried from one to the other.',
    },
  ],
} as const;
